/**
 * AssignConversationDialog - Modal para atribuir conversa a um agente
 * 
 * Permite selecionar um agente disponível para atender a conversa
 * com opção de atribuir para si mesmo
 */

import { useState, useEffect } from 'react';
import { UserPlus, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { QueueItem } from '@/hooks/useServiceQueue';
import { logger } from '@/lib/logger';

interface AssignConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: QueueItem | null;
  onAssign: (conversationId: string, agentId: string) => Promise<boolean>;
}

interface Agent {
  id: string;
  full_name: string | null;
  email: string;
}

export function AssignConversationDialog({
  open,
  onOpenChange,
  conversation,
  onAssign,
}: AssignConversationDialogProps) {
  const { user, currentTenant } = useAuth();
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // v2.0.0: Membros do tenant via hook centralizado (cache compartilhado, RPC server-side)
  const { data: tenantMembers = [], isLoading: loading } = useTenantMembers({
    onlyActive: true,
    enabled: !!currentTenant?.id && open,
  });
  const agents: Agent[] = tenantMembers.map(m => ({
    id: m.user_id,
    full_name: m.full_name,
    email: m.email || '',
  }));

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setSelectedAgentId('');
    }
  }, [open]);

  // Atribui para si mesmo
  const handleAssignToMe = async () => {
    if (!conversation || !user?.id) return;

    setSubmitting(true);
    const success = await onAssign(conversation.id, user.id);
    setSubmitting(false);

    if (success) {
      onOpenChange(false);
    }
  };

  // Atribui para o agente selecionado
  const handleAssign = async () => {
    if (!conversation || !selectedAgentId) return;

    setSubmitting(true);
    const success = await onAssign(conversation.id, selectedAgentId);
    setSubmitting(false);

    if (success) {
      onOpenChange(false);
    }
  };

  // Extrai iniciais do nome
  const getInitials = (name: string | null, email: string): string => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Atribuir Conversa
          </DialogTitle>
          <DialogDescription>
            {conversation?.contact?.name || conversation?.contact?.phone || 'Contato'} - 
            Selecione um agente para atender esta conversa
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Botão Atribuir para Mim */}
          <Button
            variant="outline"
            className="w-full justify-start gap-3 h-auto py-3"
            onClick={handleAssignToMe}
            disabled={submitting}
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="font-medium">Atribuir para mim</p>
              <p className="text-xs text-muted-foreground">
                Assumir este atendimento agora
              </p>
            </div>
          </Button>

          {/* Divisor */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                ou selecione um agente
              </span>
            </div>
          </div>

          {/* Select de Agentes */}
          <Select
            value={selectedAgentId}
            onValueChange={setSelectedAgentId}
            disabled={loading}
          >
            <SelectTrigger>
              <SelectValue placeholder={loading ? "Carregando..." : "Selecione um agente"} />
            </SelectTrigger>
            <SelectContent>
              {agents
                .filter(a => a.id !== user?.id) // Exclui o usuário atual
                .map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-[10px] bg-muted">
                          {getInitials(agent.full_name, agent.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span>{agent.full_name || agent.email}</span>
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedAgentId || submitting}
          >
            {submitting ? 'Atribuindo...' : 'Atribuir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
